/**
 * The long-offline notice.
 *
 * A screen keeps playing from its cache for as long as it has power, whether or not it can reach
 * the server. After a long time without contact its content and schedule are probably stale, so
 * the player says so on screen — over the content, which keeps playing — until it reaches the
 * server again. Enabled per host (the Android player turns it on; see `RuntimeHostInfo`).
 *
 * "Contact" is the last successful exchange with the Signage API: a heartbeat, a content sync or
 * pairing. It is persisted, so a reboot or a power cut does not reset the count.
 *
 * The device clock cannot be trusted on its own: a box without a clock battery boots at some old
 * date until it gets the time from the internet, and anyone can set a TV's clock by hand. So two
 * measures are kept and the notice shows when either passes the limit:
 *
 * - Wall-clock time since the last contact, counted only when it is believable: zero when the
 *   clock reads earlier than the last contact (it went backwards, or the box booted at its
 *   factory date), and ignored when it reads more than `IMPLAUSIBLE_CLOCK_GAP_MS` later (a clock
 *   set wildly into the future).
 * - Running time since the last contact: how long the player has actually been running, measured
 *   with a monotonic clock that no clock change moves, and persisted across restarts. It covers
 *   the cases where the wall clock is ignored, at the cost of not counting time spent switched
 *   off.
 *
 * Both are checked again every minute, so a screen that runs for months without a restart still
 * shows the notice on time, and both reset the moment the server answers.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** How long a screen may go without reaching the server before it says so. The one place to change it. */
export const OFFLINE_NOTICE_AFTER_DAYS = 90;
export const OFFLINE_NOTICE_AFTER_MS = OFFLINE_NOTICE_AFTER_DAYS * DAY_MS;

/** A wall-clock gap longer than this is taken as a wrong clock rather than a real absence. */
export const IMPLAUSIBLE_CLOCK_GAP_MS = 3 * 365 * DAY_MS;

/** How often the runtime re-checks while it runs. */
export const OFFLINE_CHECK_INTERVAL_MS = 60_000;

export interface OfflineMeasure {
  /** Device wall clock now, ms since the epoch. */
  nowMs: number;
  /** Last contact with the server by the same clock, or null if there has never been one. */
  lastContactMs: number | null;
  /** Running time since the last contact, from the monotonic clock. */
  runningMs: number;
}

/** Wall-clock time since the last contact, or 0 when the clock cannot be believed. */
export function wallClockOfflineMs(nowMs: number, lastContactMs: number | null): number {
  if (lastContactMs === null) return 0;
  const gap = nowMs - lastContactMs;
  if (!(gap > 0) || gap > IMPLAUSIBLE_CLOCK_GAP_MS) return 0;
  return gap;
}

/** The best estimate of how long the screen has been out of contact. */
export function offlineForMs(measure: OfflineMeasure): number {
  return Math.max(
    wallClockOfflineMs(measure.nowMs, measure.lastContactMs),
    Math.max(0, measure.runningMs || 0),
  );
}

/** Whether the notice should be on screen. A screen that never reached the server is exempt. */
export function shouldShowOfflineNotice(measure: OfflineMeasure, afterMs: number): boolean {
  if (measure.lastContactMs === null) return false;
  return offlineForMs(measure) >= afterMs;
}
